import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Tag, ArrowLeft, ArrowRight, Loader2, Phone, MessageCircle, Sparkles, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import { SOCIAL } from "../data/siteData";
import { getPillarForCategory } from "../data/curriculumGuidesMap";

export default function BlogDetail() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setBlog(null);
        api.get(`/blogs/${slug}`)
            .then((r) => {
                setBlog(r.data);
                // fetch related
                return api.get("/blogs").then((rr) => {
                    const all = rr.data || [];
                    const rel = all.filter((b) => b.slug !== r.data.slug && (b.category === r.data.category || (b.tags || []).some((t) => (r.data.tags || []).includes(t)))).slice(0, 3);
                    if (rel.length < 3) {
                        const others = all.filter((b) => b.slug !== r.data.slug && !rel.includes(b)).slice(0, 3 - rel.length);
                        setRelated([...rel, ...others]);
                    } else {
                        setRelated(rel);
                    }
                });
            })
            .catch(() => setError("Article not found."))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>;
    }
    if (error || !blog) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
                <div>
                    <p className="text-slate-600 mb-4">{error}</p>
                    <Link to="/blog" className="text-[#1E3A8A] font-semibold">← Back to Blog</Link>
                </div>
            </div>
        );
    }

    // Build BlogPosting + FAQPage schema
    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": `https://anandtutorials.com/post/${blog.slug}`,
        "url": `https://anandtutorials.com/post/${blog.slug}`,
        "headline": blog.title,
        "description": blog.excerpt,
        "image": blog.cover_image,
        "datePublished": blog.created_at,
        "dateModified": blog.updated_at || blog.created_at,
        "author": blog.author_meta ? {
            "@type": "Person",
            "name": blog.author_meta.name,
            "jobTitle": blog.author_meta.role,
            "description": blog.author_meta.bio,
            "worksFor": { "@type": "Organization", "name": "Anand Tutorials" },
        } : { "@type": "Organization", "name": blog.author || "Anand Tutorials" },
        "publisher": {
            "@type": "Organization",
            "name": "Anand Tutorials",
            "logo": { "@type": "ImageObject", "url": "https://anandtutorials.com/logo-anand-tutorials.png" },
        },
        "mainEntityOfPage": `https://anandtutorials.com/post/${blog.slug}`,
        "keywords": (blog.tags || []).join(", "),
    };

    const faqSchema = blog.faqs && blog.faqs.length ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": blog.faqs.map(([q, a]) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a },
        })),
    } : null;

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://anandtutorials.com/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://anandtutorials.com/blog" },
            { "@type": "ListItem", "position": 3, "name": blog.title, "item": `https://anandtutorials.com/post/${blog.slug}` },
        ],
    };

    return (
        <div data-testid="blog-detail-page">
            <SEO
                title={blog.title}
                description={blog.excerpt}
                keywords={(blog.tags || []).join(", ")}
                path={`/post/${blog.slug}`}
                type="article"
                image={blog.cover_image}
                schema={articleSchema}
            />
            {faqSchema && <SEO schema={faqSchema} />}
            <SEO schema={breadcrumbSchema} />

            <article className="bg-white">
                {blog.cover_image && (
                    <div className="aspect-[21/9] max-h-[480px] w-full overflow-hidden bg-slate-100">
                        <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
                    </div>
                )}
                <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
                    <Link to="/blog" className="inline-flex items-center gap-1.5 text-[#1E3A8A] font-semibold mb-6 hover:gap-2.5 transition-all">
                        <ArrowLeft className="w-4 h-4" /> All Articles
                    </Link>

                    {/* Breadcrumb visual */}
                    <nav className="text-xs text-slate-500 mb-4" aria-label="Breadcrumb">
                        <Link to="/" className="hover:text-[#1E3A8A]">Home</Link> <span className="mx-1.5">/</span>
                        <Link to="/blog" className="hover:text-[#1E3A8A]">Blog</Link> <span className="mx-1.5">/</span>
                        <span className="text-slate-700">{blog.category || "Article"}</span>
                    </nav>

                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-4 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-[#1E3A8A] rounded font-semibold"><Tag className="w-3 h-3" /> {blog.category || "General"}</span>
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(blog.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
                        <span>By {blog.author || "Anand Tutorials"}</span>
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-[#0F172A] leading-tight">{blog.title}</h1>
                    <p className="text-lg text-slate-600 leading-relaxed mb-8 border-l-4 border-amber-400 pl-4 italic">{blog.excerpt}</p>

                    {/* Curriculum-pillar bidirectional CTA — visible only on the
                        five "Curriculum · <X>" articles. Links back to the
                        parent curriculum pillar page and to the sub-hub. */}
                    {(() => {
                        const pillar = getPillarForCategory(blog.category);
                        if (!pillar) return null;
                        return (
                            <aside
                                data-testid="blog-curriculum-pillar-cta"
                                className="mb-8 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-5 sm:p-6"
                            >
                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-700 mb-2">
                                    <BookOpen className="w-3 h-3" /> Part of the {pillar.curriculum} · {pillar.region} pillar
                                </div>
                                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                                    <span className="text-xl leading-none">{pillar.flag}</span>
                                    <Link
                                        to={pillar.pillarPath}
                                        data-testid="blog-curriculum-pillar-link"
                                        className="font-heading font-bold text-[#0F172A] hover:text-emerald-700 text-lg leading-snug inline-flex items-center gap-1.5"
                                    >
                                        {pillar.pillarLabel}
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                                    This deep-dive sits inside our full {pillar.curriculum} curriculum pillar — syllabus structure, subject-mix trade-offs, mentor pool and pricing.
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <Link
                                        to={pillar.pillarPath}
                                        data-testid="blog-curriculum-pillar-cta-primary"
                                        className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
                                    >
                                        {pillar.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                    <Link
                                        to="/blog/category/curriculum-guides"
                                        data-testid="blog-curriculum-subhub-link"
                                        className="inline-flex items-center gap-1.5 bg-white border border-emerald-200 hover:border-emerald-500 text-emerald-700 text-sm font-semibold px-4 py-2 rounded-lg"
                                    >
                                        All curriculum deep-dives <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                </div>
                            </aside>
                        );
                    })()}

                    {/* Author byline card */}
                    {blog.author_meta && (
                        <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl mb-8" data-testid="author-byline">
                            <div
                                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-heading font-bold text-sm shrink-0"
                                style={{ backgroundColor: blog.author_meta.avatar_color }}
                            >
                                {blog.author_meta.initials}
                            </div>
                            <div className="text-sm">
                                <div className="font-heading font-semibold text-[#0F172A]">{blog.author_meta.name}</div>
                                <div className="text-xs text-slate-500">{blog.author_meta.role} · {blog.author_meta.credentials}</div>
                            </div>
                        </div>
                    )}

                    <div className="blog-content prose-lg" dangerouslySetInnerHTML={{ __html: blog.content }} />

                    {blog.tags?.length > 0 && (
                        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
                            {blog.tags.map((t) => (
                                <span key={t} className="px-3 py-1 rounded-full bg-blue-50 text-[#1E3A8A] text-xs font-medium">#{t}</span>
                            ))}
                        </div>
                    )}

                    {/* Author full bio card at end of article */}
                    {blog.author_meta && blog.author_meta.bio && (
                        <div className="mt-8 grid grid-cols-[auto_1fr] gap-5 p-6 bg-gradient-to-br from-slate-50 to-blue-50/40 border border-slate-200 rounded-2xl" data-testid="author-bio-card">
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center text-white font-heading font-bold text-lg shrink-0"
                                style={{ backgroundColor: blog.author_meta.avatar_color }}
                            >
                                {blog.author_meta.initials}
                            </div>
                            <div>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6] mb-1">About the Author</div>
                                <div className="font-heading font-bold text-[#0F172A] text-lg">{blog.author_meta.name}</div>
                                <div className="text-xs text-slate-500 mb-2">{blog.author_meta.role} · {blog.author_meta.credentials}</div>
                                <p className="text-sm text-slate-700 leading-relaxed">{blog.author_meta.bio}</p>
                            </div>
                        </div>
                    )}

                    {/* Inline lead magnet CTA */}
                    <div className="mt-10 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-2xl p-7 text-white" data-testid="blog-cta">
                        <div className="inline-flex items-center gap-1.5 bg-amber-500 text-[#0F172A] text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full mb-3">
                            <Sparkles className="w-3 h-3" /> Try a free 1:1 demo
                        </div>
                        <h3 className="font-heading text-xl sm:text-2xl font-bold mb-2 leading-snug">Want a custom plan for your child?</h3>
                        <p className="text-blue-100 text-sm leading-relaxed mb-5">
                            Book a free 1:1 demo class with a verified Anand Tutorials mentor. We'll diagnose your child's gaps in 20 minutes and design a study plan — no payment, no commitment.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <Link to="/register" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-bold px-5 py-2.5 rounded-lg hover:bg-amber-400">
                                Book Free Demo <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a href="tel:+917000530316" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-white/15">
                                <Phone className="w-4 h-4" /> Call Us
                            </a>
                            <a
                                href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent(`Hi Anand Tutorials, I just read "${blog.title}" and would like to know more.`)}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#1ebe5a]"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                        </div>
                    </div>
                </div>

                {/* Related posts */}
                {related.length > 0 && (
                    <section className="bg-[#F8FAFC] border-t border-slate-200 py-14">
                        <div className="max-w-6xl mx-auto px-6 lg:px-8">
                            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-2">More from the Anand Tutorials Blog</h3>
                            <p className="text-sm text-slate-500 mb-8">Hand-picked articles related to this one.</p>
                            <div className="grid md:grid-cols-3 gap-5">
                                {related.map((b) => (
                                    <Link key={b.id} to={`/post/${b.slug}`} data-testid={`related-${b.slug}`} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-blue-200 transition-all flex flex-col">
                                        {b.cover_image && (
                                            <div className="aspect-[16/9] overflow-hidden">
                                                <img src={b.cover_image} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            </div>
                                        )}
                                        <div className="p-5 flex-1 flex flex-col">
                                            <span className="text-[10px] uppercase tracking-[0.15em] text-[#3B82F6] font-bold mb-1.5">{b.category}</span>
                                            <h4 className="font-heading font-semibold text-[#0F172A] leading-snug group-hover:text-[#1E3A8A] line-clamp-2 mb-2">{b.title}</h4>
                                            <p className="text-xs text-slate-500 line-clamp-2 flex-1">{b.excerpt}</p>
                                            <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#1E3A8A] group-hover:gap-2 transition-all">
                                                Read article <ArrowRight className="w-3.5 h-3.5" />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </article>
        </div>
    );
}
